import { HttpClient }                   from '@angular/common/http';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild
}                                       from '@angular/core';
import { DomSanitizer, SafeHtml }       from '@angular/platform-browser';
import { SafeMethods }                  from '@cs/common';
import {
	FilterCompareBarQuery,
	FilterCompareBarService
}                                       from '@cs/components/filter-and-compare-bar';
import { CsToastManagerService }        from '@cs/components/toast-manager';
import { isNullOrUndefined }            from '@cs/core';
import { AppQuery }                     from '@cs/performance-manager/shared';
import { ShellConfigService }           from '@cs/performance-manager/shell';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService }             from '@ngx-translate/core';
import { Subscription, fromEvent }      from 'rxjs';

@UntilDestroy()
@Component({
												selector:        'pmc-iframe-documentation',
												templateUrl:     './iframe-documentation.component.html',
												changeDetection: ChangeDetectionStrategy.OnPush
											})
export class IframeDocumentationComponent
	implements OnInit, OnDestroy, AfterViewInit {
	@ViewChild('iframe', {static: true, read: ElementRef})
	iframe: ElementRef<HTMLIFrameElement>;

	srcUrl: SafeHtml;

	iframeReceiver = '';
	iframeSender   = window.location.origin;
	docusaurusHash = '#page';

	constructor(
		protected readonly toastService: CsToastManagerService,
		readonly filterCompareBarQuery: FilterCompareBarQuery,
		readonly filterCompareBarService: FilterCompareBarService,
		readonly appQuery: AppQuery,
		public readonly changeRef: ChangeDetectorRef,
		protected readonly i8n: TranslateService,
		private sanitizer: DomSanitizer,
		private readonly shellConfig: ShellConfigService,
		private http: HttpClient
	) {}

	ngOnInit() {
		this.shellConfig.enableFooter = false;
		this.srcUrl                   = this.sanitizer.bypassSecurityTrustResourceUrl('');

		this.navbarChangeSubscription = this.appQuery
																																						.select((x) => x.currentAppParamsScope)
																																						.pipe(untilDestroyed(this))
																																						.subscribe((value) => {
																																							if (isNullOrUndefined(value)) return;

																																							const {location}    = value;
																																							this.iframeReceiver = location.replace(/\/docs/i, '');
																																							this.srcUrl         = this.sanitizer.bypassSecurityTrustResourceUrl(
																																								this.getCurrentHash(location)
																																							);
																																							SafeMethods.detectChanges(this.changeRef);
																																						});
	}

	getCurrentHash(location: string): string {
		const hash        = window.location.hash;
		this.lastLocation = location;

		if (hash.startsWith(this.docusaurusHash)) {
			const docusaurusPath = hash.substring(this.docusaurusHash.length);
			return `${location}${docusaurusPath}`;
		}
		return location;
	}

	ngAfterViewInit(): void {
		this.iframe.nativeElement.onload = () => {
			this.handleParentHashChange(true);

			// ✅ Use RxJS instead of manual addEventListener
			fromEvent(window, 'hashchange')
				.pipe(untilDestroyed(this))
				.subscribe(() => {
					const newUrl = this.getCurrentHash(this.lastLocation);
					if (this.lastUrl != null && this.lastUrl !== newUrl) {
						this.srcUrl = this.sanitizer.bypassSecurityTrustResourceUrl(newUrl);
						SafeMethods.detectChanges(this.changeRef);
					}
				});

			fromEvent<MessageEvent>(window, 'message')
				.pipe(untilDestroyed(this))
				.subscribe((event) => {
					const data = event.data;
					if (data && data.type === 'DOCUSAURUS_NAVIGATED' && data.path) {
						const newParentHash = `${this.docusaurusHash}${data.path}`;
						if (window.location.hash !== newParentHash) {
							window.location.hash = newParentHash;
							this.lastUrl         = encodeURI(`${this.lastLocation}${data.path}`);
						}
					}
				});
		};
	}

	handleParentHashChange(firstRun = false) {
		// No-op now, since RxJS handles subscriptions
	}

	ngOnDestroy() {
		if (!isNullOrUndefined(this.navbarChangeSubscription)) {
			this.navbarChangeSubscription.unsubscribe();
		}

		this.shellConfig.enableFooter = true;
		// ✅ No need to manually remove event listeners — untilDestroyed handles it
	}

	private navbarChangeSubscription: Subscription;
	private bodyClasses: Array<string> = ['bg-app-background'];
	private lastLocation: string;
	private lastUrl: string;
}
